import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Title,
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  TreeCheckbox,
  CHECKBOX_STATES,
  Loader,
} from '@common/components';
import { useGetAllCountriesQuery } from '@dashboard/api/countries';
import { useTranslation } from 'react-i18next';
import filterClasses from './CentersModal.module.scss';

export default function CentersModal({
  show,
  project,
  centers,
  onClose,
  onConfirm,
  onLoaded,
}) {
  const { t, i18n } = useTranslation('dashboard\\MainFilters');
  const { t: tModal } = useTranslation('dashboard\\CentersModal');

  const [visible, setVisible] = useState(show || false);

  const [selectedProject, setSelectedProject] = useState(project);
  const [selectedCenters, setSelectedCenters] = useState(centers || []);
  const { data, isLoading, isSuccess } = useGetAllCountriesQuery({
    refetchOnFocus: true,
  });
  const [checkedTreeState, setCheckedTreeState] = useState({});
  const [countries, setCountries] = useState([]);
  const [availableCenters, setAvailableCenters] = useState([]);

  const sortCountries = (newCountries) => {
    const sortedCountries = [...newCountries]
      .map((cx) => ({
        ...cx,
        centers: [...cx.centers].sort((c1, c2) =>
          t(c1.name).localeCompare(t(c2.name)),
        ),
      }))
      .sort((c1, c2) => t(c1.code).localeCompare(t(c2.code)));

    return sortedCountries;
  };

  const fnSortCountries = () => {
    if (countries && countries.length > 0)
      setCountries(sortCountries(countries));
    else setCountries([]);
  };

  const filterCountriesbyProject = (cntries, prj) => {
    const existingCountries = [...cntries]
      .map((country) => {
        const newCountry = { ...country };
        newCountry.centers = [...country.centers].filter(
          (center) => center.project_id === prj.uuid,
        );
        return newCountry;
      })
      .filter((country) => country.centers.length > 0);
    return sortCountries(existingCountries);
  };

  const updateCenters = () => {
    let centersFiltered = [];
    countries.forEach((c) => {
      centersFiltered = [...centersFiltered, ...c.centers];
    });
    if (onLoaded) {
      setAvailableCenters(centersFiltered);
      onLoaded(centersFiltered);
    }
  };

  useEffect(() => {
    i18n.on('languageChanged', fnSortCountries);
    i18n.on('loaded', fnSortCountries);

    return () => {
      i18n.off('languageChanged', fnSortCountries);
      i18n.off('loaded', fnSortCountries);
    };
  }, []);

  useEffect(() => {
    setSelectedProject(project);
  }, [project]);

  useEffect(() => {
    setSelectedCenters(
      centers ? centers.map((c) => (c && c.uuid ? c.uuid : c)) : undefined,
    );
  }, [centers]);

  useEffect(() => {
    if (isSuccess && project) {
      setCountries(
        data && data.data && data.data.length
          ? filterCountriesbyProject(data.data, selectedProject)
          : null,
      );
    }
  }, [data, selectedProject]);

  useEffect(() => {
    updateCenters();
  }, [countries]);

  const refreshFilterState = () => {
    const selectedItems = {};
    countries.forEach((country) => {
      let checkedCount = 0;
      country.centers.forEach((center) => {
        const isChecked = selectedCenters.indexOf(center.uuid) > -1;
        if (isChecked) checkedCount += 1;
        selectedItems[center.uuid] = isChecked
          ? CHECKBOX_STATES.Checked
          : CHECKBOX_STATES.Empty;
      });

      if (checkedCount >= 1) {
        if (checkedCount < country.centers.length) {
          selectedItems[country.uuid] = CHECKBOX_STATES.Indeterminate;
        } else {
          selectedItems[country.uuid] = CHECKBOX_STATES.Checked;
        }
      } else {
        selectedItems[country.uuid] = CHECKBOX_STATES.Empty;
      }
    });

    setCheckedTreeState(selectedItems);
  };

  let centerStateLocked = false;
  const handleSelectCenterOrCountry = (status, item) => {
    if (!centerStateLocked) {
      centerStateLocked = true;
      if (status === CHECKBOX_STATES.Empty) {
        if (item.code) {
          const countryCenters = item.centers.map((c) => c.uuid);
          setSelectedCenters(
            selectedCenters.filter((c) => countryCenters.indexOf(c) === -1),
          );
        } else {
          setSelectedCenters(selectedCenters.filter((c) => c !== item.uuid));
        }
      } else if (status === CHECKBOX_STATES.Checked) {
        if (item.code) {
          const countryCenters = item.centers.map((c) => c.uuid);

          setSelectedCenters([...selectedCenters, ...countryCenters]);
        } else {
          setSelectedCenters([...selectedCenters, item.uuid]);
        }
      }
    }
  };

  const handleAccept = () => {
    setVisible(false);
    if (onConfirm) {
      onConfirm(selectedCenters);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const handleClose = () => {
    setVisible(false);
    if (onClose) onClose();
  };

  useEffect(() => {
    setVisible(show || false);
  }, [show]);

  useEffect(() => {
    refreshFilterState();

    return () => {
      centerStateLocked = false;
    };
  }, [selectedCenters, show]);

  return (
    <Modal onClose={handleClose} visible={visible}>
      <ModalHeader>
        <Title className={filterClasses.title}>
          <div>{tModal('lblCentersTitle')}</div>
          <div className={filterClasses.remaining}>
            {tModal('lblModalCentersRemaining', {
              selected:
                selectedCenters && selectedCenters.length
                  ? selectedCenters.length
                  : 0,
              total:
                availableCenters && availableCenters.length
                  ? availableCenters.length
                  : 0,
            })}
          </div>
        </Title>
      </ModalHeader>
      <ModalContent className={filterClasses.center}>
        <div className={filterClasses.main}>
          <div className={filterClasses.container}>
            <div className={filterClasses.body}>
              {isLoading ? (
                <Loader />
              ) : (
                <TreeCheckbox
                  checkedStatus={checkedTreeState}
                  items={countries}
                  label={(item) => (item.code ? t(item.code) : item.name)}
                  onChange={(status, item) =>
                    handleSelectCenterOrCountry(status, item)
                  }
                />
              )}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter className={filterClasses.center}>
        <Button variant="outline" color="error" onClick={handleCancel}>
          {tModal('btnCancelLabel')}
        </Button>
        <Button variant="outline" color="accent" onClick={handleAccept}>
          {tModal('btnOkLabel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

CentersModal.propTypes = {
  project: PropTypes.object.isRequired,
  centers: PropTypes.array,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onLoaded: PropTypes.func,
  onConfirm: PropTypes.func,
};
